import { useMemo, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Calculator as CalcIcon, ArrowRight, TrendDown, TrendUp, Minus } from "@phosphor-icons/react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "../lib/api";

const money = (v) =>
  "$" + Math.round(v).toLocaleString(undefined, { maximumFractionDigits: 0 });

function Slider({ label, value, set, min, max, step, format, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label htmlFor={testId} className="text-sm font-semibold text-foreground/70">{label}</label>
        <span className="font-serif text-xl text-primary" data-testid={`${testId}-value`}>{format(value)}</span>
      </div>
      <input
        id={testId}
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full h-1.5 appearance-none bg-border rounded-full cursor-pointer accent-accent"
      />
    </div>
  );
}

export default function Calculator() {
  const [price, setPrice] = useState(24000);
  const [years, setYears] = useState(15);
  const [fees, setFees] = useState(1400);
  const [other, setOther] = useState(200);
  const [nights, setNights] = useState(7);
  const [rate, setRate] = useState(280);

  const calc = useMemo(() => {
    const totalNights = years * nights;
    const totalCost = price + years * (fees + other);
    const perNight = totalNights > 0 ? totalCost / totalNights : 0;
    const publicTotal = totalNights * rate;
    const diff = totalCost - publicTotal;
    const ratio = rate > 0 ? perNight / rate : 0;
    let verdict = null;
    if (rate > 0) {
      if (ratio <= 0.85)
        verdict = { tone: "good", title: "The numbers work", body: "At this price you're beating the public booking rate comfortably. This is what a rational purchase looks like — provided the usage assumptions are honest." };
      else if (ratio <= 1.15)
        verdict = { tone: "even", title: "Roughly break-even", body: "You're paying about what the same nights would cost to book publicly. The ownership adds commitment without meaningful savings — negotiate the price down or walk away free." };
      else
        verdict = { tone: "bad", title: "Cheaper to just book", body: "At these figures, the ownership costs more than booking the same holidays as a regular guest. That premium is the sales presentation talking, not the maths." };
    }
    return { totalNights, totalCost, perNight, publicTotal, diff, verdict };
  }, [price, years, fees, other, nights, rate]);

  const spring = useSpring(calc.perNight, { stiffness: 90, damping: 22 });
  const perNightDisplay = useTransform(spring, (v) => money(v));

  const VerdictIcon = calc.verdict?.tone === "good" ? TrendDown : calc.verdict?.tone === "bad" ? TrendUp : Minus;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>The Sanity Check</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.08]" data-testid="calculator-heading">
            What does a night <em className="text-accent">really</em> cost you?
          </h2>
          <p className="mt-6 text-foreground/70 max-w-xl">
            Run any offer — developer or resale — through this before you sign. If the cost per night
            doesn't beat booking the same resort as a regular guest, the deal doesn't work.
          </p>
        </Reveal>

        <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-6 space-y-8" delay={0.1}>
            <Slider label="Purchase price" value={price} set={setPrice} min={0} max={100000} step={500} format={money} testId="calc-price" />
            <Slider label="Years you'll realistically use it" value={years} set={setYears} min={1} max={30} step={1} format={(v) => `${v} yrs`} testId="calc-years" />
            <Slider label="Annual maintenance fees" value={fees} set={setFees} min={0} max={5000} step={50} format={(v) => `${money(v)}/yr`} testId="calc-fees" />
            <Slider label="Other annual fees (exchange, club, booking)" value={other} set={setOther} min={0} max={2000} step={25} format={(v) => `${money(v)}/yr`} testId="calc-other" />
            <Slider label="Nights you actually use per year" value={nights} set={setNights} min={1} max={30} step={1} format={(v) => `${v} nights`} testId="calc-nights" />
            <Slider label="Public booking rate for a comparable night" value={rate} set={setRate} min={50} max={800} step={10} format={(v) => `${money(v)}/night`} testId="calc-rate" />
            <p className="text-xs text-foreground/45 leading-relaxed max-w-md" data-testid="calculator-note">
              Tip: find the public rate by pricing the same resort, same season, on any major booking site.
              Use real figures from your own statements — fee inflation makes optimism expensive.
            </p>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={0.2}>
            <div className="bg-primary text-primary-foreground rounded-sm p-10 md:p-14 relative overflow-hidden" data-testid="calculator-result-card">
              <CalcIcon size={120} weight="duotone" className="absolute -right-6 -top-6 text-primary-foreground/10" />
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground/50">Your true cost per night</p>
              <div className="mt-6 flex items-baseline gap-2">
                <motion.span className="font-serif text-7xl md:text-8xl tracking-tight" data-testid="calc-cost-per-night">
                  {perNightDisplay}
                </motion.span>
                <span className="text-primary-foreground/50 text-sm font-semibold">/ night</span>
              </div>
              <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 text-sm border-t border-primary-foreground/15 pt-8">
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/45">Total cost of ownership</dt>
                  <dd className="mt-1.5 font-serif text-2xl" data-testid="calc-total-cost">{money(calc.totalCost)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/45">Total nights</dt>
                  <dd className="mt-1.5 font-serif text-2xl" data-testid="calc-total-nights">{calc.totalNights.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/45">Booking publicly instead</dt>
                  <dd className="mt-1.5 font-serif text-2xl" data-testid="calc-public-total">{money(calc.publicTotal)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/45">{calc.diff >= 0 ? "Ownership premium" : "Ownership saving"}</dt>
                  <dd className="mt-1.5 font-serif text-2xl" data-testid="calc-difference">{money(Math.abs(calc.diff))}</dd>
                </div>
              </dl>
              {calc.verdict && (
                <motion.div
                  key={calc.verdict.tone}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-10 bg-primary-foreground/10 border border-primary-foreground/15 rounded-sm p-6 flex items-start gap-4"
                  data-testid="calc-verdict"
                >
                  <VerdictIcon size={26} weight="bold" className="shrink-0 mt-0.5 text-accent" />
                  <div>
                    <p className="font-serif text-xl" data-testid="calc-verdict-title">{calc.verdict.title}</p>
                    <p className="mt-2 text-sm text-primary-foreground/70 leading-relaxed">{calc.verdict.body}</p>
                  </div>
                </motion.div>
              )}
              <button
                data-testid="calculator-cta-consult"
                onClick={() => scrollToId("consult")}
                className="group mt-10 inline-flex items-center gap-3 bg-accent text-accent-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-background hover:text-primary transition-colors duration-300"
              >
                Sense-check an offer with us
                <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
